export class Color {
  public r: number; // 0～255
  public g: number; // 0～255
  public b: number; // 0～255
  public a: number; // 0～255

  public get alpha() {
    return this.a / 255.0;
  }
  public set alpha(value: number) {
    this.a = Math.min(Math.max(value, 0.0), 1.0) * 255.0;
  }

  constructor(r: number, g: number, b: number, a: number);
  constructor(code: string);
  constructor(hex: number);
  constructor() {
    switch (arguments.length) {
      case 0:
        this.r = 0;
        this.g = 0;
        this.b = 0;
        this.a = 0;
        break;
      case 1:
        if (typeof arguments[0] === "string") {
          const code = arguments[0];
          if (code.length === "#000000".length) {
            this.r = this.hexStrToDecNum(code.slice(1, 3));
            this.g = this.hexStrToDecNum(code.slice(3, 5));
            this.b = this.hexStrToDecNum(code.slice(5, 7));
            this.a = 255;
          } else if (code.length === "#00000000".length) {
            this.a = this.hexStrToDecNum(code.slice(1, 3));
            this.r = this.hexStrToDecNum(code.slice(3, 5));
            this.g = this.hexStrToDecNum(code.slice(5, 7));
            this.b = this.hexStrToDecNum(code.slice(7, 9));
          } else {
            throw new Error("invalid color code format.");
          }
        } else {
          const hex = arguments[0];
          this.r = (hex >> 0) & 0xff;
          this.g = (hex >> 8) & 0xff;
          this.b = (hex >> 16) & 0xff;
          this.a = (hex >> 24) & 0xff;
        }
        break;
      case 3:
        this.r = arguments[0];
        this.g = arguments[1];
        this.b = arguments[2];
        this.a = 255;
        break;
      case 4:
        this.r = arguments[0];
        this.g = arguments[1];
        this.b = arguments[2];
        this.a = arguments[3];
        break;
      default:
        throw new Error("invalid arguments.");
    }
  }

  public toU32() {
    return (
      ((this.r & 0xff) << 0) |
      ((this.g & 0xff) << 8) |
      ((this.b & 0xff) << 16) |
      ((this.a & 0xff) << 24)
    );
  }

  public toArray4() {
    return [this.r / 255.0, this.g / 255.0, this.b / 255.0, this.a / 255.0];
  }

  public equal(value: Color) {
    return (
      this.r === value.r &&
      this.g === value.g &&
      this.b === value.b &&
      this.a === value.a
    );
  }

  public hexStrToDecNum(str: string) {
    const s1 = this.hexCharToDecNum(str.charCodeAt(0));
    const s2 = this.hexCharToDecNum(str.charCodeAt(1));
    return s1 * 16 + s2;
  }

  public hexCharToDecNum(c: number) {
    const f = function (a: string) {
      return a.charCodeAt(0);
    };
    const c0 = f("0");
    const c9 = f("9");

    const cA = f("A");
    const cZ = f("Z");

    const ca = f("a");
    const cz = f("z");

    if (c0 <= c && c <= c9) {
      return c - c0;
    } else if (cA <= c && c <= cZ) {
      return c - cA + 10;
    } else if (ca <= c && c <= cz) {
      return c - ca + 10;
    } else {
      throw new Error("invalid char.");
    }
  }

  // css向けのコードに変換します
  public code() {
    return (
      "rgba(" + this.r + "," + this.g + "," + this.b + "," + this.a / 255 + ")"
    );
  }

  public clone() {
    return new Color(this.r, this.g, this.b, this.a);
  }

  public mulAlpha(value: number) {
    return new Color(this.r, this.g, this.b, this.a * value);
  }

  public toString() {
    return this.code;
  }

  public static Lerp(c1: Color, c2: Color, amount: number) {
    return new Color(
      Math.floor((c2.r - c1.r) * amount + c1.r),
      Math.floor((c2.g - c1.g) * amount + c1.g),
      Math.floor((c2.b - c1.b) * amount + c1.b),
      Math.floor((c2.a - c1.a) * amount + c1.a)
    );
  }

  public static Transparent: ColorR = new Color(0, 0, 0, 0);
  public static White: ColorR = new Color(255, 255, 255, 255);
  public static Black: ColorR = new Color(0, 0, 0, 255);
  public static Red: ColorR = new Color(255, 0, 0, 255);
  public static Green: ColorR = new Color(0, 128, 0, 255);
  public static Blue: ColorR = new Color(0, 0, 255, 255);
  public static Lime: ColorR = new Color(0, 255, 0, 255);
  public static DimGray: ColorR = new Color(105, 105, 105, 255);
  public static Gray: ColorR = new Color(128, 128, 128, 255); // white 50%
  public static DarkGray: ColorR = new Color(169, 169, 169, 255);
  public static Orange: ColorR = new Color(255, 165, 0, 255);
  public static DarkOrange: ColorR = new Color(255, 140, 0, 255);
  public static Gold: ColorR = new Color(255, 215, 0, 255);
  public static Khaki: ColorR = new Color(240, 230, 140, 255);
  public static Yellow: ColorR = new Color(255, 212, 0, 255);
  public static CornflowerBlue: ColorR = new Color(100, 149, 237, 255);
  public static SmokeBlue: ColorR = new Color(164, 193, 215, 255);
  public static RitchBlue: ColorR = new Color(51, 102, 204, 255); //#3366cc
  public static RitchRed: ColorR = new Color(220, 57, 18, 255); //#DC3912
  public static RitchOrange: ColorR = new Color(255, 153, 0, 255); //#FF9900
  public static RitchGreen: ColorR = new Color(16, 150, 24, 255); //#109618
}

type ColorR = Readonly<Color>;
